// src/components/shared/GameStatsEditor.js
// The box-score editor for one league game: sport, final score, and a stat
// table per side. Extracted from OfficialDashboard 2026-09-15, when the league
// console needed the same form — polyp-official-games' per-game gate was
// widened so a league owner reaches GET/POST /official/games/{id}/stats for
// ANY game in their league, not just one they scorekept themselves.
//
// Two hosts, one component:
//   /official  — OfficialDashboard, outside `.client-page` (no theme tokens)
//   /league    — LeagueSchedulePage, inside it (tokens, and a light mode)
// which is why GameStatsEditor.css writes every color as
// `var(--token, #fallback)`; see the comment there.
//
// The prop is `matchId` rather than `gameId` because the two envelopes
// disagree: polyp-league's game rows call it `gameId`, the official routes call
// the same id `matchId`. Callers pass whichever they hold.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ApiService from "../../services/api.service";
import { SPORT_STATS, ACTIVE_SPORTS, DEFAULT_SPORT } from "../../config/sportStats";
import "./GameStatsEditor.css";

export default function GameStatsEditor({
  matchId,
  teamAName,
  teamBName,
  onSaved,
  onCancel,
  cancelLabel = "Cancel",
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState(DEFAULT_SPORT);
  const [scoreA, setScoreA] = useState("");
  const [scoreB, setScoreB] = useState("");
  const [statValues, setStatValues] = useState({}); // { [clientId]: { [statKey]: string } }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Set when the server answers 409 gameCompleted: somebody else's result is on
  // the row. Holds the payload so "Replace their result" can resend it with the
  // override rather than rebuilding it.
  const [conflict, setConflict] = useState(null);

  // Guards against a stale fetch landing after the caller switched games.
  const version = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const mine = ++version.current;
    setLoading(true);
    setError(null);
    setConflict(null);
    (async () => {
      try {
        const res = await ApiService.getOfficialMatchStats(matchId);
        if (cancelled || mine !== version.current) return;
        setDetail(res);
        setSport(ACTIVE_SPORTS.includes(res?.sport) ? res.sport : DEFAULT_SPORT);
        setScoreA(typeof res?.scoreA === "number" ? String(res.scoreA) : "");
        setScoreB(typeof res?.scoreB === "number" ? String(res.scoreB) : "");
        const init = {};
        for (const p of res?.players || []) {
          const row = {};
          for (const [k, v] of Object.entries(p.stats || {})) {
            row[k] = v === null || v === undefined ? "" : String(v);
          }
          init[p.clientId] = row;
        }
        setStatValues(init);
      } catch (err) {
        if (cancelled || mine !== version.current) return;
        setError(err.message || "Failed to load game detail.");
      } finally {
        if (!cancelled && mine === version.current) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [matchId]);

  const setStat = (clientId, key, value) => {
    // Allow empty; otherwise keep digits only.
    const clean = value === "" ? "" : value.replace(/[^\d]/g, "");
    setStatValues((prev) => ({ ...prev, [clientId]: { ...prev[clientId], [key]: clean } }));
  };

  const columns = useMemo(() => SPORT_STATS[sport] || [], [sport]);
  const teamAPlayers = useMemo(() => (detail?.players || []).filter((p) => p.team === "A"), [detail]);
  const teamBPlayers = useMemo(() => (detail?.players || []).filter((p) => p.team === "B"), [detail]);

  const post = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      await ApiService.saveOfficialMatchStats(matchId, payload);
      setConflict(null);
      if (onSaved) await onSaved();
    } catch (err) {
      // 409 gameCompleted — another account recorded this result. Not a failure
      // to report: the caller may replace it, they just have to say so. The
      // server never infers that from their role, which is why the league owner
      // lands here too rather than silently overwriting an official's work.
      if (err.status === 409 && err.code === "gameCompleted") {
        setConflict(payload);
      } else {
        setError(err.message || "Failed to save stats.");
      }
    } finally {
      setSaving(false);
    }
  }, [matchId, onSaved]);

  const save = () => {
    const players = (detail?.players || []).map((p) => {
      const raw = statValues[p.clientId] || {};
      const stats = {};
      for (const col of columns) {
        if (raw[col.key] !== undefined && raw[col.key] !== "") stats[col.key] = Number(raw[col.key]);
      }
      return { clientId: p.clientId, businessId: p.businessId, team: p.team, stats };
    });
    // League games are never rated (no Glicko) — see the HARD RULE in
    // backend/polyp-official-games/index.mjs's file header.
    post({
      sport,
      scoreA: scoreA === "" ? 0 : Number(scoreA),
      scoreB: scoreB === "" ? 0 : Number(scoreB),
      players,
    });
  };

  if (loading) return <p className="polyp-stats-editor__empty">Loading roster…</p>;
  if (!detail) {
    return (
      <div className="polyp-stats-editor">
        <p className="polyp-stats-editor__error">{error || "Failed to load game detail."}</p>
        {onCancel && (
          <button type="button" className="polyp-stats-editor__button" onClick={onCancel}>
            {cancelLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="polyp-stats-editor">
      <div className="polyp-stats-editor__controls">
        <div className="polyp-stats-editor__control">
          <label className="polyp-stats-editor__label" htmlFor={`sport-${matchId}`}>Sport</label>
          <select
            id={`sport-${matchId}`}
            className="polyp-stats-editor__input"
            value={sport}
            onChange={(e) => setSport(e.target.value)}
          >
            {ACTIVE_SPORTS.map((s) => (
              <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="polyp-stats-editor__control">
          <span className="polyp-stats-editor__badge polyp-stats-editor__badge--a">A</span>
          <input
            type="text" inputMode="numeric" placeholder="0" aria-label="Team A score"
            className="polyp-stats-editor__input polyp-stats-editor__score"
            value={scoreA} onChange={(e) => setScoreA(e.target.value.replace(/[^\d]/g, ""))}
          />
          <span className="polyp-stats-editor__label">–</span>
          <input
            type="text" inputMode="numeric" placeholder="0" aria-label="Team B score"
            className="polyp-stats-editor__input polyp-stats-editor__score"
            value={scoreB} onChange={(e) => setScoreB(e.target.value.replace(/[^\d]/g, ""))}
          />
          <span className="polyp-stats-editor__badge polyp-stats-editor__badge--b">B</span>
        </div>
      </div>

      <TeamTable
        side="A" name={detail.teamA?.businessName || teamAName}
        players={teamAPlayers} columns={columns} statValues={statValues} setStat={setStat}
      />
      <TeamTable
        side="B" name={detail.teamB?.businessName || teamBName}
        players={teamBPlayers} columns={columns} statValues={statValues} setStat={setStat}
      />

      <div className="polyp-stats-editor__actions">
        {onCancel && (
          <button type="button" className="polyp-stats-editor__button" onClick={onCancel}>
            {cancelLabel}
          </button>
        )}
        <button
          type="button"
          className="polyp-stats-editor__button polyp-stats-editor__button--primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save stats"}
        </button>
      </div>

      {error && <p className="polyp-stats-editor__error">{error}</p>}

      {conflict && (
        <div className="polyp-stats-editor__conflict">
          <p>
            These stats were submitted by the official who scorekept this game.
            Saving replaces their result — their box score will be overwritten with yours.
          </p>
          <div className="polyp-stats-editor__actions">
            <button type="button" className="polyp-stats-editor__button" onClick={() => setConflict(null)}>
              Keep theirs
            </button>
            <button
              type="button"
              className="polyp-stats-editor__button polyp-stats-editor__button--danger"
              onClick={() => post({ ...conflict, overrideRecordedBy: true })}
              disabled={saving}
            >
              {saving ? "Saving…" : "Replace their result"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TeamTable({ side, name, players, columns, statValues, setStat }) {
  return (
    <div className="polyp-stats-editor__team">
      <div className="polyp-stats-editor__team-head">
        <span className={`polyp-stats-editor__badge polyp-stats-editor__badge--${side.toLowerCase()}`}>{side}</span>
        <strong>{name}</strong>
      </div>
      {players.length === 0 ? (
        <p className="polyp-stats-editor__empty">No participants on this side.</p>
      ) : (
        <div className="polyp-stats-editor__scroll">
          <table className="polyp-stats-editor__table">
            <thead>
              <tr>
                <th className="polyp-stats-editor__player-head">Player</th>
                {columns.map((col) => (
                  <th key={col.key} title={col.label}>{col.abbr}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.clientId}>
                  <td className="polyp-stats-editor__player-cell">{p.name}</td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        aria-label={`${p.name} ${col.label}`}
                        className="polyp-stats-editor__stat-input"
                        value={(statValues[p.clientId] || {})[col.key] ?? ""}
                        onChange={(e) => setStat(p.clientId, col.key, e.target.value)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
